/**
 * Configuration Processor - Simplified Single Processing Pipeline
 *
 * This module controls ALL configuration processing with a single, clean architecture:
 *
 * CORE INSIGHT: There is only ONE candidate processing method used everywhere.
 * The only difference between "phases" is which shapes we try.
 *
 * PROCESSING PIPELINE:
 * 1. Shape Selection (different strategies: 3 predefined vs iterative search)
 * 2. Universal Processing (CandidateProcessor.processCandidate() - identical for all)
 * 3. Optional Optimization (independent + automatic modes)
 *
 * This replaces the overcomplicated three-layer architecture with a simple,
 * unified approach that reflects how the system actually works.
 */

import { buildFirstFitCandidates } from '~/builders/candidate-builder';
import { logger } from '~/services/logging';
import type { Candidate, Configuration, Project } from '~/types';
import type { BaseProjectElements } from '~/types/candidate';
import {
  ConfigurationOptimizer,
  type PipelineOptimizationConfig,
} from './managers/optimizers/configuration-optimizer';
import { ShapeSearchManager } from './managers/shape-search-manager';

export interface ConfigurationProcessorOptions {
  // Shape selection strategy
  useIterativeShapeSearch?: boolean; // true = best-fit search, false = first-fit only
  maxShapesToTry?: number; // Limit for iterative search

  // Optimization
  enableAutomaticOptimization?: boolean;
  optimizationConfig?: Partial<PipelineOptimizationConfig>;

  // General
  enableLogging?: boolean;
}

export interface ConfigurationProcessorResult {
  success: boolean;
  configuration: Configuration;
  processing: {
    shapesGenerated: number;
    candidatesProcessed: number;
    processingTime: number;
    bestCandidate: Candidate | null;
  };
  optimization?: {
    wasApplied: boolean;
    candidatesGenerated: number;
    finalCandidate: Candidate | null;
    processingTime: number;
  };
  totalProcessingTime: number;
  error?: string;
}

export class ConfigurationProcessor {
  /**
   * Complete configuration processing - single entry point for all processing
   * This is the ONLY method needed to process a configuration
   */
  static async processConfiguration(
    configuration: Configuration,
    project: Project,
    baseElements: BaseProjectElements,
    options: ConfigurationProcessorOptions = {}
  ): Promise<ConfigurationProcessorResult> {
    const startTime = Date.now();
    const {
      useIterativeShapeSearch = true,
      maxShapesToTry = 50,
      enableAutomaticOptimization = true,
      enableLogging = true,
    } = options;

    if (enableLogging) {
      logger.L2.info(`Starting configuration processing`, {
        configurationId: configuration.id,
        strategy: useIterativeShapeSearch ? 'Iterative shape search' : 'First-fit shapes only',
      });
      logger.phase.enter('configuration-processing');
    }

    try {
      // STEP 1: Process shapes using the selected strategy
      const processingResult = await ConfigurationProcessor.processShapes(
        configuration,
        project,
        baseElements,
        {
          useIterativeSearch: useIterativeShapeSearch,
          maxShapes: maxShapesToTry,
          enableLogging,
        }
      );

      if (!processingResult.success) {
        throw new Error(processingResult.error || 'Shape processing failed');
      }

      if (enableLogging) {
        logger.L2.info(`Shape processing completed`, {
          shapesGenerated: processingResult.shapesGenerated,
          candidatesProcessed: processingResult.candidatesProcessed,
          bestCandidate: processingResult.bestCandidate?.name || null,
        });
      }

      // STEP 2: Optional automatic optimization
      let optimizationResult: ConfigurationProcessorResult['optimization'];

      if (enableAutomaticOptimization && processingResult.bestCandidate) {
        optimizationResult = await ConfigurationProcessor.optimizeCandidate(
          configuration,
          project,
          processingResult.bestCandidate,
          options.optimizationConfig || {}
        );

        if (optimizationResult.wasApplied && enableLogging) {
          logger.L2.info(`Optimization completed`, {
            candidatesGenerated: optimizationResult.candidatesGenerated,
            finalCandidate: optimizationResult.finalCandidate?.name || null,
          });
        }
      }

      const totalProcessingTime = Date.now() - startTime;

      if (enableLogging) {
        logger.phase.exit('configuration-processing', {
          phase: 'configuration-processing',
          duration: totalProcessingTime,
        });
        logger.L2.info(`Processing complete`, {
          totalCandidates: configuration.candidates.length,
          processingTimeMs: totalProcessingTime,
        });
      }

      return {
        success: true,
        configuration,
        processing: {
          shapesGenerated: processingResult.shapesGenerated,
          candidatesProcessed: processingResult.candidatesProcessed,
          processingTime: processingResult.processingTime,
          bestCandidate: optimizationResult?.finalCandidate || processingResult.bestCandidate,
        },
        optimization: optimizationResult,
        totalProcessingTime,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown processing error';
      logger.L2.error(`Processing failed`, {
        error: errorMessage,
        configurationId: configuration.id,
      });

      return {
        success: false,
        configuration,
        processing: {
          shapesGenerated: 0,
          candidatesProcessed: 0,
          processingTime: Date.now() - startTime,
          bestCandidate: null,
        },
        totalProcessingTime: Date.now() - startTime,
        error: errorMessage,
      };
    }
  }

  /**
   * Process shapes using the specified strategy
   * This is where the actual shape selection and processing happens
   */
  private static async processShapes(
    configuration: Configuration,
    project: Project,
    baseElements: BaseProjectElements,
    options: {
      useIterativeSearch: boolean;
      maxShapes: number;
      enableLogging?: boolean;
    }
  ): Promise<{
    success: boolean;
    shapesGenerated: number;
    candidatesProcessed: number;
    processingTime: number;
    bestCandidate: Candidate | null;
    error?: string;
  }> {
    const startTime = Date.now();
    const { useIterativeSearch, maxShapes, enableLogging = false } = options;

    try {
      if (useIterativeSearch) {
        // STRATEGY: Iterative shape search (best-fit algorithm)
        if (enableLogging) {
          logger.L2.debug(`Using iterative shape search`, {
            maxShapes,
          });
        }

        // First get the initial candidates (3 first-fit shapes)
        const initialCandidates = buildFirstFitCandidates(project, configuration);
        configuration.candidates = initialCandidates;
        configuration.totalCandidates = initialCandidates.length;

        if (enableLogging) {
          logger.L2.debug(`Generated initial candidates`, {
            candidateCount: initialCandidates.length,
          });
        }

        // Then use best-fit algorithm to find better shapes
        const projectDataForShapeGeneration = {
          dateRecords: baseElements.dateRecords,
          dateRecordsMapByPaymentDate: baseElements.dateRecordsMapByPaymentDate,
          taxableValueMap: baseElements.taxableValueMap,
          dateInputs: project.dateInputs?.startingTaxYear
            ? {
                startingTaxYear: parseInt(project.dateInputs.startingTaxYear, 10),
              }
            : undefined,
        };

        const shapeSearchManager = new ShapeSearchManager();
        const bestFitResult = await shapeSearchManager.refineToBestShape(
          configuration.candidates,
          baseElements,
          configuration,
          projectDataForShapeGeneration,
          project
        );

        if (bestFitResult.success && bestFitResult.bestFitCandidate) {
          // Add all generated candidates to the configuration
          const updatedCandidates = bestFitResult.allCandidates;
          configuration.candidates = updatedCandidates;
          configuration.totalCandidates = updatedCandidates.length;

          const processingTime = Date.now() - startTime;

          return {
            success: true,
            shapesGenerated: bestFitResult.searchMetrics.shapesGenerated,
            candidatesProcessed: updatedCandidates.length,
            processingTime,
            bestCandidate: bestFitResult.bestFitCandidate,
          };
        } else {
          throw new Error('Best-fit processing failed');
        }
      } else {
        // STRATEGY: First-fit shapes only (3 predefined shapes)
        if (enableLogging) {
          logger.L2.debug(`Using first-fit shapes only`, {
            shapeCount: 3,
          });
        }

        const candidates = buildFirstFitCandidates(project, configuration);
        configuration.candidates = candidates;
        configuration.totalCandidates = candidates.length;

        // Find the best of the 3 first-fit candidates
        const bestCandidate = candidates.reduce((best, current) => {
          if (!best.results.isViable && current.results.isViable) return current;
          if (best.results.isViable && !current.results.isViable) return best;
          return current.results.totalPrincipal > best.results.totalPrincipal ? current : best;
        });

        const processingTime = Date.now() - startTime;

        return {
          success: true,
          shapesGenerated: 3,
          candidatesProcessed: candidates.length,
          processingTime,
          bestCandidate,
        };
      }
    } catch (error) {
      const processingTime = Date.now() - startTime;

      return {
        success: false,
        shapesGenerated: 0,
        candidatesProcessed: configuration.candidates.length,
        processingTime,
        bestCandidate: null,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Optimize a specific candidate - can be called independently or automatically
   * This makes optimization available both as part of the automatic pipeline
   * and as a standalone operation for manual optimization
   */
  static async optimizeCandidate(
    configuration: Configuration,
    project: Project,
    candidate: Candidate,
    config: Partial<PipelineOptimizationConfig> = {}
  ): Promise<{
    wasApplied: boolean;
    candidatesGenerated: number;
    finalCandidate: Candidate | null;
    processingTime: number;
  }> {
    const startTime = Date.now();

    try {
      // Execute unified optimization pipeline
      const optimizationResult = ConfigurationOptimizer.runOptimizationPipeline(
        candidate,
        project,
        configuration,
        {
          utilizationConfig: config.utilizationConfig,
          fiveYearRuleConfig: config.fiveYearRuleConfig,
          millageRedistributionConfig: config.millageRedistributionConfig,
          enableLogging: config.enableLogging,
        }
      );

      if (optimizationResult.success) {
        // Add all generated candidates to configuration
        const updatedCandidates = [
          ...configuration.candidates,
          ...optimizationResult.allGeneratedCandidates,
        ];
        configuration.candidates = updatedCandidates;
        configuration.totalCandidates = updatedCandidates.length;
      }

      const processingTime = Date.now() - startTime;

      return {
        wasApplied: optimizationResult.success,
        candidatesGenerated: optimizationResult.allGeneratedCandidates.length,
        finalCandidate: optimizationResult.finalCandidate,
        processingTime,
      };
    } catch (error) {
      const processingTime = Date.now() - startTime;
      logger.L2.error(`Optimization failed`, {
        error: error instanceof Error ? error.message : String(error),
      });

      return {
        wasApplied: false,
        candidatesGenerated: 0,
        finalCandidate: candidate, // Return original candidate on failure
        processingTime,
      };
    }
  }

  /**
   * Convenience method: Process with first-fit shapes only
   */
  static async processFirstFitOnly(
    configuration: Configuration,
    project: Project,
    baseElements: BaseProjectElements
  ): Promise<ConfigurationProcessorResult> {
    return ConfigurationProcessor.processConfiguration(configuration, project, baseElements, {
      useIterativeShapeSearch: false,
      enableAutomaticOptimization: false,
    });
  }

  /**
   * Convenience method: Process with full iterative search + optimization
   */
  static async processComplete(
    configuration: Configuration,
    project: Project,
    baseElements: BaseProjectElements
  ): Promise<ConfigurationProcessorResult> {
    return ConfigurationProcessor.processConfiguration(configuration, project, baseElements, {
      useIterativeShapeSearch: true,
      enableAutomaticOptimization: true,
    });
  }
}
